import type {FC, ReactNode} from 'react'
import {createContext, useContext} from 'react'

import type {IndexingState} from '@containers/Dashboards/AgentChat/components/chatMessageList/indexingNotice'

const IndexingContext = createContext<IndexingState>({})

/**
 * The documents this turn is waiting to finish reading, and how far along each
 * one is (AB#5992).
 *
 * A context rather than props because the state is owned by the message list
 * and needed by one attachment card several memoised layers down; threading it
 * through every row to reach one chip would make each of those components care
 * about something none of them do.
 */
export const IndexingProvider: FC<{
  documents: IndexingState
  children: ReactNode
}> = ({documents, children}) => (
  <IndexingContext.Provider value={documents}>{children}</IndexingContext.Provider>
)

/**
 * Whether this document is being read, and how far along.
 *
 * `percent` is null when ingest has not yet worked out how much work the file
 * is — the card says it is reading, without a number it cannot stand behind.
 */
export const useReadingProgress = (
  documentId: string | null | undefined,
): {reading: boolean; percent: number | null} => {
  const documents = useContext(IndexingContext)
  if (!documentId || !(documentId in documents)) return {reading: false, percent: null}
  return {reading: true, percent: documents[documentId]}
}
